package com.rosario.juridico.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.marketplace.model.Expediente
import com.rosario.juridico.marketplace.model.EstadosProcesales
import com.rosario.juridico.marketplace.model.FuerosJudiciales
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.navigation.Routes

import kotlinx.coroutines.launch

private val AzulPrimario = Color(0xFF1565C0)
private val AzulFondo    = Color(0xFFF0F4FF)
private val GrisNeutro    = Color(0xFF546E7A)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaNuevoExpediente(navController: NavController) {
    var caratula       by remember { mutableStateOf("") }
    var nroExpediente  by remember { mutableStateOf("") }
    var cliente        by remember { mutableStateOf("") }
    var fuero          by remember { mutableStateOf("") }
    var juzgado        by remember { mutableStateOf("") }
    var estadoProcesal by remember { mutableStateOf("") }
    var prioridad      by remember { mutableStateOf("Normal") }
    var observaciones  by remember { mutableStateOf("") }
    var loading        by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    // ── VERIFICACIÓN DE EMAIL Y LOGIN ──
    val isLoggedIn = remember { FirebaseProxy.isLoggedIn() }
    


    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Nuevo Expediente", color = Color.White, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Atrás", tint = Color.White)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = AzulPrimario)
            )
        }
    ) { padding ->
        if (!isLoggedIn) {
            Box(modifier = Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                Card(
                    modifier = Modifier.fillMaxWidth().padding(24.dp),
                    shape = RoundedCornerShape(20.dp),
                    colors = CardDefaults.cardColors(containerColor = AzulFondo)
                ) {
                    Column(
                        modifier = Modifier.padding(28.dp),
                        verticalArrangement = Arrangement.spacedBy(14.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            if (!isLoggedIn) Icons.Default.Lock else Icons.Default.MarkEmailUnread, 
                            null, 
                            tint = AzulPrimario, 
                            modifier = Modifier.size(48.dp)
                        )
                        Text(
                            if (!isLoggedIn) "Acceso Restringido" else "Modo Lectura Activo",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = AzulPrimario
                        )
                        Text(
                            if (!isLoggedIn) "Debes iniciar sesión para crear expedientes."
                            else "Por favor, verificá tu correo electrónico para habilitar nuevamente la creación de expedientes.",
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.bodyMedium,
                            color = GrisNeutro
                        )
                        Button(
                            onClick = { navController.navigate(Routes.login(register = false)) },
                            colors = ButtonDefaults.buttonColors(containerColor = AzulPrimario),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text(if (!isLoggedIn) "Iniciar Sesión" else "Verificar Cuenta", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .padding(horizontal = 20.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Spacer(Modifier.height(8.dp))

                // ── Sección: Identificación ────────────────────────────────────────
                SeccionFormulario(titulo = "Identificación del Expediente", icono = Icons.Default.Folder)

                OutlinedTextField(
                    value = caratula,
                    onValueChange = { caratula = it },
                    label = { Text("Carátula *") },
                    placeholder = { Text("Ej: Pérez, Juan c/ García, María s/ Daños y Perjuicios") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.Gavel, null, tint = AzulPrimario) },
                    singleLine = false,
                    maxLines = 3,
                    isError = caratula.isBlank()
                )

                OutlinedTextField(
                    value = nroExpediente,
                    onValueChange = { nroExpediente = it },
                    label = { Text("Número de Expediente") },
                    placeholder = { Text("Ej: 12345/2024") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.Tag, null, tint = AzulPrimario) },
                    singleLine = true
                )

                OutlinedTextField(
                    value = cliente,
                    onValueChange = { cliente = it },
                    label = { Text("Cliente / Parte") },
                    placeholder = { Text("Nombre del cliente o parte representada") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.Person, null, tint = AzulPrimario) },
                    singleLine = true
                )

                // ── Sección: Jurisdicción ──────────────────────────────────────────
                Spacer(Modifier.height(4.dp))
                SeccionFormulario(titulo = "Jurisdicción", icono = Icons.Default.AccountBalance)

                SelectDropdown(
                    label = "Fuero",
                    opciones = FuerosJudiciales.todos,
                    seleccionado = fuero,
                    onSeleccionar = { fuero = it },
                    icono = Icons.Default.Gavel
                )

                OutlinedTextField(
                    value = juzgado,
                    onValueChange = { juzgado = it },
                    label = { Text("Juzgado / Tribunal") },
                    placeholder = { Text("Ej: Juzgado Civil N° 5 - Rosario") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.AccountBalance, null, tint = AzulPrimario) },
                    singleLine = true
                )

                // ── Sección: Estado y Prioridad ────────────────────────────────────
                Spacer(Modifier.height(4.dp))
                SeccionFormulario(titulo = "Estado y Prioridad", icono = Icons.Default.Flag)

                SelectDropdown(
                    label = "Estado Procesal",
                    opciones = EstadosProcesales.todos,
                    seleccionado = estadoProcesal,
                    onSeleccionar = { estadoProcesal = it },
                    icono = Icons.Default.Timeline
                )

                // Chips de prioridad
                Column {
                    Text(
                        "Prioridad",
                        style = MaterialTheme.typography.labelMedium,
                        color = Color.Gray,
                        modifier = Modifier.padding(start = 4.dp, bottom = 8.dp)
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        listOf("Alta" to Color(0xFFC62828), "Normal" to AzulPrimario, "Baja" to Color(0xFF2E7D32))
                            .forEach { (nivel, color) ->
                                FilterChip(
                                    selected = prioridad == nivel,
                                    onClick = { prioridad = nivel },
                                    label = { Text(nivel) },
                                    colors = FilterChipDefaults.filterChipColors(
                                        selectedContainerColor = color.copy(alpha = 0.15f),
                                        selectedLabelColor = color
                                    )
                                )
                            }
                    }
                }

                // ── Sección: Observaciones ─────────────────────────────────────────
                Spacer(Modifier.height(4.dp))
                SeccionFormulario(titulo = "Observaciones", icono = Icons.Default.Notes)

                OutlinedTextField(
                    value = observaciones,
                    onValueChange = { observaciones = it },
                    label = { Text("Observaciones (opcional)") },
                    placeholder = { Text("Notas adicionales sobre el expediente...") },
                    modifier = Modifier.fillMaxWidth().heightIn(min = 100.dp),
                    shape = RoundedCornerShape(12.dp),
                    singleLine = false,
                    maxLines = 5
                )

                Spacer(Modifier.height(8.dp))

                // ── Botón guardar ──────────────────────────────────────────────────
                Button(
                    onClick = {
                        if (loading) return@Button
                        val uid = FirebaseProxy.getCurrentUserId()
                        if (uid == null) {
                            scope.launch {
                                snackbarHostState.showSnackbar("Debes iniciar sesión para guardar expedientes")
                                navController.navigate(Routes.LOGIN)
                            }
                            return@Button
                        }
                        loading = true
                        scope.launch {
                            try {
                                FirebaseProxy.addExpediente(
                                    userId = uid,
                                    expediente = Expediente(
                                        caratula = caratula.trim(),
                                        nroExpediente = nroExpediente.trim(),
                                        cliente = cliente.trim(),
                                        fuero = fuero,
                                        juzgado = juzgado.trim(),
                                        estadoProcesal = estadoProcesal,
                                        prioridad = prioridad,
                                        observaciones = observaciones.trim(),
                                        fechaCreacion = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
                                    )
                                )
                                navController.popBackStack()
                            } catch (e: Exception) {
                                snackbarHostState.showSnackbar("Error al guardar: ${e.message}")
                            } finally {
                                loading = false
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(54.dp),
                    enabled = caratula.isNotBlank() && !loading,
                    shape = RoundedCornerShape(16.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = AzulPrimario)
                ) {
                    if (loading) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                    } else {
                        Icon(Icons.Default.Save, null)
                        Spacer(Modifier.width(8.dp))
                        Text("Guardar Expediente", fontWeight = FontWeight.Bold)
                    }
                }

                Spacer(Modifier.height(16.dp))
            }
        }
    }
}

// ── Componente: encabezado de sección ─────────────────────────────────────────
@Composable
private fun SeccionFormulario(
    titulo: String,
    icono: androidx.compose.ui.graphics.vector.ImageVector
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Icon(icono, null, tint = AzulPrimario, modifier = Modifier.size(18.dp))
        Text(
            titulo,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = AzulPrimario
        )
    }
    HorizontalDivider(color = AzulPrimario.copy(alpha = 0.2f))
}

// ── Componente: select desplegable ────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SelectDropdown(
    label: String,
    opciones: List<String>,
    seleccionado: String,
    onSeleccionar: (String) -> Unit,
    icono: androidx.compose.ui.graphics.vector.ImageVector
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it }
    ) {
        OutlinedTextField(
            value = seleccionado,
            onValueChange = {},
            readOnly = true,
            label = { Text(label) },
            leadingIcon = { Icon(icono, null, tint = AzulPrimario) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            modifier = Modifier.fillMaxWidth().menuAnchor(),
            shape = RoundedCornerShape(12.dp)
        )
        ExposedDropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            opciones.forEach { opcion ->
                DropdownMenuItem(
                    text = { Text(opcion) },
                    onClick = { onSeleccionar(opcion); expanded = false },
                    leadingIcon = {
                        if (opcion == seleccionado) {
                            Icon(Icons.Default.Check, null, tint = AzulPrimario, modifier = Modifier.size(16.dp))
                        }
                    }
                )
            }
        }
    }
}
